import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { H3, Text, typographyHelper } from '@property.com.au/ui-kit';
import { usePropertyPageModalContext } from '../../../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { ContentBrick } from '../../../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { trackDisclaimerModalOpen } from '../../../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { DynamicContent } from '../../../../../app-kit/components/DynamicContent/DynamicContent';
import { EnergyEfficiencyGauge } from './subcomponents/EnergyEfficiencyGauge/EnergyEfficiencyGauge';
import {
  DynamicContents,
  DynamicContentsItem,
} from '../../../../../app-kit/components/DynamicContent/types';

type EnergyEfficiencyBrickProps = {
  title: string;
  subtitle: string;
  description: DynamicContents<DynamicContentsItem>;
  energyEfficiency: number;
};

const Description = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const EnergyEfficiencyTitleWrapper = styled(H3)`
  ${typographyHelper('body01Bold')}
`;

const SubtitleWithDynamicContent = styled(Text)`
  > a {
    ${typographyHelper('body01')};
  }
`;

const EnergyEfficiencyTitle: FunctionComponent<PropsWithChildren<{ title: string }>> = ({
  title,
}) => {
  return <EnergyEfficiencyTitleWrapper variant="subtitle01">{title}</EnergyEfficiencyTitleWrapper>;
};

export const EnergyEfficiencyBrick: FunctionComponent<EnergyEfficiencyBrickProps> = ({
  title,
  subtitle,
  description,
  energyEfficiency,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  return (
    <ContentBrick
      title={title}
      titleComponent={<EnergyEfficiencyTitle title={title} />}
      subtitle={subtitle}
      isSubBrick={true}
      headerChildren={
        <DisclaimerIcon
          title="Open Energy Efficiency Disclaimer"
          onClick={() => {
            displayModal({ name: 'energyEfficiencyDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__energy_efficiency_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      <Description>
        <SubtitleWithDynamicContent>
          <DynamicContent dynamicContents={description} textColorVariant="textSecondary" />
        </SubtitleWithDynamicContent>
        <EnergyEfficiencyGauge energyEfficiency={energyEfficiency} />
      </Description>
    </ContentBrick>
  );
};
